import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader } from "../../components/ui/Loader";
import { fetchPartners, PartnerWithTotals } from "../../lib/admin";
import { formatMoney } from "../../lib/projects";

export const AdminPartners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<PartnerWithTotals[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPartners()
      .then(setPartners)
      .catch((e) => setError(e instanceof Error ? e.message : "Помилка завантаження"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="mx-auto w-full max-w-cabinet flex-1 px-5 py-8 md:px-8">
      <h1 className="text-2xl font-bold text-white">Партнери</h1>

        {loading ? (
          <Loader />
        ) : error ? (
          <p className="mt-7 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </p>
        ) : partners.length === 0 ? (
          <p className="card mt-7 px-5 py-12 sm:p-12 text-center text-sm text-neutral-400">
            Партнерів ще немає
          </p>
        ) : (
          <section className="card mt-5 sm:mt-7 p-4 sm:p-6">
            <div className="hidden grid-cols-[1.2fr_1fr_1fr_1.2fr_1fr_0.5fr_0.7fr_58px] items-center gap-4 border-b border-line pb-3 mb-5 text-sm text-neutral-500 md:grid">
              <span>Імʼя</span>
              <span>Компанія</span>
              <span>Телефон</span>
              <span>Email</span>
              <span className="text-center">Telegram</span>
              <span className="text-center">Проєкти</span>
              <span className="text-center">Бонуси</span>
              <span></span>
            </div>
            <div>
              {partners.map((p, idx) => (
                <div
                  key={p.id}
                  className={`cursor-pointer transition hover:opacity-90${idx > 0 ? " border-t border-line" : " pt-0"} py-4${idx === partners.length - 1 ? " pb-0" : ""}`}
                  onClick={() => navigate(`/admin/partners/${p.id}`)}
                >
                  <div className="grid grid-cols-[1fr_auto] items-start gap-x-4 gap-y-1 md:hidden">
                    <div className="min-w-0">
                      <span className="font-semibold text-white">
                        {p.name ?? "—"}
                      </span>
                      {p.email && (
                        <a
                          href={`mailto:${p.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 block text-xs text-neutral-400 hover:underline"
                        >
                          {p.email}
                        </a>
                      )}
                      {p.phone && (
                        <a
                          href={`tel:${p.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-0.5 block text-xs text-neutral-400 hover:underline"
                        >
                          {p.phone}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Link
                        to={`/admin/partners/${p.id}`}
                        className="link-brand mb-1 text-xs"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Деталі
                      </Link>
                      {p.telegram_username ? (
                        <a
                          href={`https://t.me/${p.telegram_username}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-full bg-sky-500/15 px-3 py-1 text-xs font-medium text-sky-500"
                        >
                          Telegram: {p.telegram_username}
                        </a>
                      ) : (
                        <span className="rounded-full bg-neutral-500/15 px-3 py-1 text-xs font-medium text-neutral-500">
                          Telegram: —
                        </span>
                      )}
                      <span className="rounded-full bg-green-500/15 px-3 py-1 text-xs font-medium text-green-500">
                        Бонус: {formatMoney(p.bonus_total)}
                      </span>
                    </div>
                  </div>
                  <div className="hidden md:grid md:grid-cols-[1.2fr_1fr_1fr_1.2fr_1fr_0.5fr_0.7fr_58px] md:items-center md:gap-4">
                    <span className="min-w-0 truncate font-semibold text-white">
                      {p.name ?? "—"}
                    </span>
                    <span className="truncate text-neutral-300">
                      {p.company ?? "—"}
                    </span>
                    <span className="truncate text-neutral-300">
                      {p.phone ? (
                        <a
                          href={`tel:${p.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline"
                        >
                          {p.phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </span>
                    <span className="truncate text-neutral-300">
                      {p.email ? (
                        <a
                          href={`mailto:${p.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline"
                        >
                          {p.email}
                        </a>
                      ) : (
                        "—"
                      )}
                    </span>
                    <span className="text-center">
                      {p.telegram_username ? (
                        <a
                          href={`https://t.me/${p.telegram_username}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="truncate text-sm font-medium text-green-500 hover:underline"
                        >
                          {p.telegram_username}
                        </a>
                      ) : (
                        <span className="truncate text-sm text-neutral-500">—</span>
                      )}
                    </span>
                    <span className="text-center text-neutral-200">
                      {p.project_count}
                    </span>
                    <span className="text-center text-neutral-200">
                      {formatMoney(p.bonus_total)}
                    </span>
                    <Link
                      to={`/admin/partners/${p.id}`}
                      className="link-brand justify-self-end px-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Деталі
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
    </main>
  );
};
